import React, { lazy } from "react";
import CalendarDays from "../icon/CalendarDays";
import UserGroup from "../icon/UserGroup";
import Users from "../icon/Users";
import BuildingOffice from "../icon/BuildingOffice";
import Document from "../icon/Document";
import CreditCardIcon from "../icon/CreditCardIcon";
import HomeIcon from "../icon/HomeIcon";
import TaskIcon from "../icon/TaskIcon";
import EfromIcon from "../icon/EfromIcon";
import ArrowTrendingUpIcon from "../icon/ArrowTrendingUpIcon";
import WhatsappRawIcon from "../icon/WhatsappRawIcon";
import ChatIcon from "../icon/ChatIcon";
import ShoppingBagIcon from "../icon/ShoppingBagIcon";
import SparkleIcon from "../icon/SparkleIcon";
import {
    adminRole,
    memberRole,
    ownerRole,
    staffRole,
    IS_COMMERCE_HIDDEN
} from "../utils/constants";
import {
    URL_AI_AGENT,
    URL_ANALYTICS,
    URL_API_MANAGEMENT,
    URL_APPOINTMENT_RESCHEDULE,
    URL_BOOKING,
    URL_CALENDAR,
    URL_CHANGE_PASSWORD,
    URL_CHATBOT,
    URL_CLIENT_DETAILS,
    URL_CLIENTS,
    URL_CONTACT_T1,
    URL_CONTACT_T2,
    URL_CONTACT_T3,
    URL_CONTACT_WIDGET,
    URL_CREATE_E_FORM,
    URL_DOCUMENTS,
    URL_E_FORM,
    URL_EDIT_CHATBOT,
    URL_EDIT_E_FORM,
    URL_FILL_E_FORM,
    URL_FORGOT_PASSWORD,
    URL_HOME,
    URL_INTEGRATION,
    URL_LEAD_DETAILS,
    URL_Leads,
    URL_LOGIN,
    URL_AI_CAMPAIGN_DETAIL,
    URL_AI_CAMPAIGN_NEW,
    URL_AI_CAMPAIGNS,
    URL_New_WHATSAPP_TEMPLATE,
    URL_NOTICE,
    URL_ORDER,
    URL_ORDER_CATALOG_DETAIL,
    URL_ORGANIZATION,
    URL_PAGE_NOT_FOUND,
    URL_PAYMENT,
    URL_PRICING,
    URL_PROFILE,
    URL_REGISTER,
    URL_RESET_PASSWORD,
    URL_RESPONSE_E_FORM,
    URL_CREDITS,
    URL_SUBSCRIPTION,
    URL_SUBSCRIPTION_PROCESSING,
    URL_SUPPORT,
    URL_TASK,
    URL_NOTES,
    URL_VERIFY_EMAIL,
    URL_VIEW_E_FORM,
    URL_Whatsapp_Enquiry,
    URL_WHATSAPP_INTEGRATION
} from "./url";

const Dashboard = lazy(() => import("../pages/Dashboard/DashboardPage"));
const ChangePassword = lazy(() => import("../pages/Credentials/ChangePassword"));
const Profile = lazy(() => import("../pages/Profile/ProfilePage"));
const Subscription = lazy(() => import("../pages/Subscription/SubscriptionPage"));
const SubscriptionProcessing = lazy(() => import("../pages/Subscription/SubscriptionProcessingPage"));
const CreditsPage = lazy(() => import("../pages/Credits/CreditsPage"));
const Calendar = lazy(() => import("../pages/BigCalendar/CalendarPage"));
const Pricing = lazy(() => import("../pages/Pricingpage/PricingPage"));
const Clients = lazy(() => import("../pages/Clients/ClientsPage"));
const Leads = lazy(() => import("../pages/Lead/Lead"));
const ContactForm = lazy(() => import("../pages/ContactAppointmentWidget/ContactAppointmentWidget"));
// Shared ClientDetails (overview drawer + /contact/:id route)
const ClientsDetails = lazy(() => import("../pages/Clients/ClientDetailsRoutePage"));
const LeadDetails = lazy(() => import("../pages/Lead/LeadDetailPage"));
const LeadReschedule = lazy(() => import("../component/Appointment/Reschedule"));
const Organization = lazy(() => import("../pages/Organization/Organization"));
const Integration = lazy(() => import("../pages/Integration/IntegrationPage"));
const ApiManagement = lazy(() => import("../pages/ApiManagement/ApiManagement"));
const WhatsappIntegration = lazy(() => import("../pages/WhatsappIntegration/WhatsappIntegration"));
const DocumentsPage = lazy(() => import("../pages/Documents/DocumentsPage"));
const SupportPage = lazy(() => import("../pages/Support/SupportPage"));
const EFormList = lazy(() => import("../pages/E-form/EFormPage"));
const PaymentListPage = lazy(() => import("../pages/Payment/PaymentListPage"));
const BuildEForm = lazy(() => import("../pages/E-form/EFormBuilder"));
const GenerateEForm = lazy(() => import("../pages/E-form/EFormResponse"));
const ViewEForm = lazy(() => import("../pages/E-form/EFormPreviewNew"));
const LeadEnquiry = lazy(() => import("../pages/Enquiry/WhatsappEnquiry"));
const CreateNewWhatsappTemplate = lazy(() => import("../pages/WhatsappTemplates/CreateNewWhatsappTemplate"));
const AiCampaignListPage = lazy(() => import("../pages/AiCampaigns/AiCampaignListPage"));
const AiCampaignBuilderPage = lazy(() => import("../pages/AiCampaigns/AiCampaignBuilderPage"));
const Login = lazy(() => import("../pages/Credentials/Login"));
const PageNotFound = lazy(() => import("../pages/PageNotFound/PageNotFound"));
const Register = lazy(() => import("../pages/Credentials/Register"));
const ForgotPassword = lazy(() => import("../pages/Credentials/ForgotPassword"));
const Notice = lazy(() => import("../pages/Common/NotificationPage/NoticePage"));
const SetNewPassword = lazy(() => import("../pages/Credentials/SetNewPassword"));
const VerifyEmail = lazy(() => import("../pages/Credentials/VerifyEmail"));
const CustomFields = lazy(() => import("../pages/CustomFields/CustomFields"));
const TaskPage = lazy(() => import("../pages/Task&Notes/AllTaskListPage"));
const NotesPage = lazy(() => import("../pages/Task&Notes/AllNotesListPage"));
const DirectAppointment = lazy(() => import("../pages/ContactAppointmentWidget/DirectAppointmentPage"));
const Analytics = lazy(() => import("../pages/Analytics/AnalyticsPage"));
const CreateNewWhatsappFlow = lazy(() => import("../pages/WhatsappFlow/CreateNewWhatsappFlow"));
const EditFlow = lazy(() => import("../pages/WhatsappFlow/EditFlow"));
const OrderPage = lazy(() => import("../pages/Order/OrderPage"));
const OrderCatalogDetailPage = lazy(() => import("../pages/Order/OrderCatalogDetailPage"));
const AiAgent = lazy(() => import("../pages/AiAgent/AiAgentPage"));

export const baseRoutes = {
    private: [
        {
            id: "home",
            path: URL_HOME,
            enabled: true,
            showOnNav: true,
            showOnMobile: true,
            icon: HomeIcon,
            name: "Home",
            component: Dashboard,
            notAllowed: [],
            configuration: { padding: "L" },
        },
        {
            id: "leads",
            path: URL_Leads,
            enabled: true,
            showOnNav: true,
            showOnMobile: true,
            icon: UserGroup,
            name: "Leads",
            component: Leads,
            notAllowed: [],
        },
        {
            id: "contacts",
            path: URL_CLIENTS,
            enabled: true,
            showOnNav: true,
            showOnMobile: true,
            icon: Users,
            name: "Contacts",
            component: Clients,
            notAllowed: [],
        },
        {
            id: "calendar",
            path: URL_CALENDAR,
            enabled: true,
            showOnNav: true,
            showOnMobile: true,
            icon: CalendarDays,
            name: "Calendar",
            component: Calendar,
            notAllowed: [],
        },
        {
            id: "task",
            path: URL_TASK,
            enabled: true,
            showOnNav: true,
            showOnMobile: true,
            icon: TaskIcon,
            name: "Task",
            component: TaskPage,
            notAllowed: [],
        },
        {
            id: "notes",
            path: URL_NOTES,
            enabled: true,
            showOnNav: true,
            showOnMobile: true,
            icon: Document,
            name: "Notes",
            component: NotesPage,
            notAllowed: [],
        },
        {
            id: "whatsapp",
            path: URL_WHATSAPP_INTEGRATION,
            enabled: true,
            showOnNav: true,
            showOnMobile: false,
            icon: WhatsappRawIcon,
            name: "Whatsapp",
            component: WhatsappIntegration,
            notAllowed: [],
        },
        {
            id: "e_forms",
            path: URL_E_FORM,
            enabled: true,
            showOnNav: true,
            showOnMobile: true,
            icon: EfromIcon,
            name: "Forms",
            component: EFormList,
            notAllowed: [],
        },
        {
            id: "organization",
            path: URL_ORGANIZATION,
            enabled: true,
            showOnNav: false,
            showOnMobile: false,
            icon: BuildingOffice,
            name: "Organization",
            component: Organization,
            notAllowed: [memberRole, staffRole],
        },
        {
            id: "integration",
            path: URL_INTEGRATION,
            enabled: true,
            showOnNav: false,
            showOnMobile: false,
            icon: BuildingOffice,
            name: "Integration",
            component: Integration,
            notAllowed: [],
        },
        {
            id: "order",
            path: URL_ORDER,
            enabled: !IS_COMMERCE_HIDDEN,
            showOnNav: !IS_COMMERCE_HIDDEN,
            showOnMobile: false,
            icon: ShoppingBagIcon,
            name: "Commerce",
            component: OrderPage,
            notAllowed: [],
        },
        {
            id: "ai_agent",
            path: URL_AI_AGENT,
            enabled: true,
            showOnNav: true,
            // Mobile nav does not filter notAllowed, so keep this desktop-only.
            showOnMobile: false,
            icon: SparkleIcon,
            name: "AIAgent",
            component: AiAgent,
            notAllowed: [staffRole],
            configuration: { padding: "L" },
        },
        {
            id: "developers",
            path: URL_API_MANAGEMENT,
            enabled: true,
            showOnNav: false,
            showOnMobile: false,
            icon: CreditCardIcon,
            name: "Developers",
            component: ApiManagement,
            notAllowed: [adminRole, staffRole],
        },
        {
            id: "documents",
            path: URL_DOCUMENTS,
            enabled: true,
            showOnNav: true,
            showOnMobile: true,
            icon: Document,
            name: "Documents",
            component: DocumentsPage,
            notAllowed: [],
        },
        {
            id: "support",
            path: URL_SUPPORT,
            enabled: true,
            showOnNav: false,
            showOnMobile: false,
            icon: Document,
            name: "Support",
            component: SupportPage,
            notAllowed: [memberRole],
        },
       
        {
            id: "enquiry",
            path: URL_Whatsapp_Enquiry,
            enabled: true,
            showOnNav: false,
            showOnMobile: false,
            icon: ChatIcon,
            name: "Enquiry",
            component: LeadEnquiry,
            notAllowed: [],
        },
        {
            id: "analytics",
            path: URL_ANALYTICS,
            enabled: true,
            showOnNav: false,
            showOnMobile: false,
            icon: ArrowTrendingUpIcon,
            name: "Analytics",
            component: Analytics,
            notAllowed: [memberRole, staffRole],
        },
        {
            id: "payments",
            path: URL_PAYMENT,
            enabled: true,
            showOnNav: true,
            showOnMobile: true,
            icon: CreditCardIcon,
            name: "Payments",
            component: PaymentListPage,
            notAllowed: [memberRole, staffRole],
        },
        {
            id: "order_catalog_detail",
            path: URL_ORDER_CATALOG_DETAIL,
            enabled: !IS_COMMERCE_HIDDEN,
            showOnNav: false,
            showOnMobile: false,
            name: "Catalog detail",
            component: OrderCatalogDetailPage,
            notAllowed: [],
        },
        {
            id: "whatsapp_template",
            path: URL_New_WHATSAPP_TEMPLATE,
            enabled: true,
            showOnNav: false,
            showOnMobile: false,
            name: "Whatsapp template",
            component: CreateNewWhatsappTemplate,
            notAllowed: [],
        },
        {
            id: "ai_campaigns",
            path: URL_AI_CAMPAIGNS,
            enabled: true,
            showOnNav: false,
            showOnMobile: false,
            name: "AI campaigns",
            component: AiCampaignListPage,
            notAllowed: [],
        },
        {
            id: "ai_campaign_new",
            path: URL_AI_CAMPAIGN_NEW,
            enabled: true,
            showOnNav: false,
            showOnMobile: false,
            name: "New AI campaign",
            component: AiCampaignBuilderPage,
            notAllowed: [],
        },
        {
            id: "ai_campaign_detail",
            path: URL_AI_CAMPAIGN_DETAIL,
            enabled: true,
            showOnNav: false,
            showOnMobile: false,
            name: "AI campaign",
            component: AiCampaignBuilderPage,
            notAllowed: [],
        },
        {
            id: "chatbot_create",
            path: URL_CHATBOT,
            enabled: true,
            showOnNav: false,
            showOnMobile: false,
            name: "Create Chatbot",
            component: CreateNewWhatsappFlow,
            notAllowed: [],
        },
        {
            id: "chatbot_edit",
            path: URL_EDIT_CHATBOT,
            enabled: true,
            showOnNav: false,
            showOnMobile: false,
            name: "Edit Chatbot",
            component: EditFlow,
            notAllowed: [],
        },
        {
            id: "subscription",
            path: URL_SUBSCRIPTION,
            enabled: true,
            showOnNav: false,
            showOnMobile: false,
            name: "Subscription",
            component: Subscription,
            notAllowed: [staffRole, memberRole],
        },
        {
            id: "credits",
            path: URL_CREDITS,
            enabled: true,
            showOnNav: false,
            showOnMobile: false,
            name: "Credits",
            component: CreditsPage,
            notAllowed: [staffRole, memberRole],
        },
        {
            id: "pricing",
            path: URL_PRICING,
            enabled: true,
            showOnNav: false,
            showOnMobile: false,
            name: "Pricing",
            component: Pricing,
            notAllowed: [staffRole],
        },
        {
            id: "change_password",
            path: URL_CHANGE_PASSWORD,
            enabled: true,
            showOnNav: false,
            showOnMobile: false,
            name: "Change Password",
            component: <ChangePassword />,
            notAllowed: [],
        },
        {
            id: "profile",
            path: URL_PROFILE,
            enabled: true,
            showOnNav: false,
            showOnMobile: false,
            name: "Profile",
            component: Profile,
            notAllowed: [],
        },
        {
            id: "create_e_form",
            path: URL_CREATE_E_FORM,
            enabled: true,
            showOnNav: false,
            showOnMobile: false,
            name: "Create E-Form",
            component: BuildEForm,
            notAllowed: [],
        },
        {
            id: "view_e_form",
            path: URL_VIEW_E_FORM + "/:id",
            enabled: true,
            showOnNav: false,
            showOnMobile: false,
            name: "View E-Form",
            component: ViewEForm,
            notAllowed: [],
        },
        {
            id: "edit_e_form",
            path: URL_EDIT_E_FORM + "/:id",
            enabled: true,
            showOnNav: false,
            showOnMobile: false,
            name: "Edit",
            component: BuildEForm,
            notAllowed: [],
        },
        {
            id: "response_e_form",
            path: URL_RESPONSE_E_FORM,
            enabled: true,
            showOnNav: false,
            showOnMobile: false,
            name: "View E-Form Response",
            component: GenerateEForm,
            notAllowed: [],
        },
        {
            id: "client_details",
            path: URL_CLIENT_DETAILS,
            enabled: true,
            showOnNav: false,
            showOnMobile: false,
            name: "Details",
            component: ClientsDetails,
            notAllowed: [],
        },
        {
            id: "lead_details",
            path: URL_LEAD_DETAILS,
            enabled: true,
            showOnNav: false,
            showOnMobile: false,
            name: "Details",
            component: LeadDetails,
            notAllowed: [],
        },
        {
            id: "custom_fields",
            path: URL_CONTACT_WIDGET,
            enabled: true,
            showOnNav: false,
            showOnMobile: false,
            name: "Custom Fields",
            component: CustomFields,
            notAllowed: [],
        },
    ],
    public: [
        { id: "login", path: URL_LOGIN, enabled: true, component: Login },
        { id: "forgot_password", path: URL_FORGOT_PASSWORD, enabled: true, component: ForgotPassword },
        { id: "register", path: URL_REGISTER, enabled: true, component: Register },
        { id: "reset_password", path: URL_RESET_PASSWORD, enabled: true, component: SetNewPassword },
        { id: "verify_email", path: URL_VERIFY_EMAIL, enabled: true, component: VerifyEmail },
        { id: "notice", path: URL_NOTICE, enabled: true, component: Notice },
    ],
    shared: [
        { id: "subscription_processing", path: URL_SUBSCRIPTION_PROCESSING, enabled: true, component: SubscriptionProcessing },
        { id: "page_not_found", path: URL_PAGE_NOT_FOUND, enabled: true, component: PageNotFound },
        { id: "catch_all", path: "*", enabled: true, component: PageNotFound },
    ],
    client: [
        { id: "contact_t1", path: URL_CONTACT_T1, enabled: true, component: ContactForm, regex: new RegExp(URL_CONTACT_T1.replace(":level1", "[^/]+")) },
        { id: "contact_t2", path: URL_CONTACT_T2, enabled: true, component: ContactForm, regex: new RegExp(URL_CONTACT_T2.replace(":level1", "[^/]+").replace(":level2", "[^/]+")) },
        { id: "contact_t3", path: URL_CONTACT_T3, enabled: true, component: ContactForm, regex: new RegExp(URL_CONTACT_T3.replace(":level1", "[^/]+").replace(":level2", "[^/]+").replace(":level3", "[^/]+")) },
        { id: "booking", path: URL_BOOKING, enabled: true, component: DirectAppointment, regex: new RegExp(URL_BOOKING.replace(":level1", "[^/]+")) },
        { id: "fill_e_form", path: URL_FILL_E_FORM, enabled: true, showOnNav: false, name: "View E-Form", component: GenerateEForm },
        { id: "appointment_reschedule", path: URL_APPOINTMENT_RESCHEDULE, enabled: true, showOnNav: false, showOnMobile: false, name: "Lead Reschedule", component: LeadReschedule },
    ],
};

const ROLE_ENUM = {
    ADMIN: adminRole,
    MEMBER: memberRole,
    OWNER: ownerRole,
    STAFF: staffRole,
};

const normalizeRole = (role) => {
    if (typeof role !== "string") return null;
    const normalized = ROLE_ENUM[role.trim().toUpperCase()];
    return normalized || role;
};

const normalizeNotAllowed = (notAllowed) => {
    if (!Array.isArray(notAllowed)) return null;
    return notAllowed.map(normalizeRole).filter(Boolean);
};

const applyRouteOverride = (route, override) => {
    if (!override || typeof override !== "object") return route;

    const next = { ...route };

    if (typeof override.enabled === "boolean" && !["catch_all", "page_not_found"].includes(route.id)) next.enabled = override.enabled;
    if (typeof override.show_on_nav === "boolean") next.showOnNav = override.show_on_nav;
    else if (typeof override.showOnNav === "boolean") next.showOnNav = override.showOnNav;
    if (typeof override.show_on_mobile === "boolean") next.showOnMobile = override.show_on_mobile;
    else if (typeof override.showOnMobile === "boolean") next.showOnMobile = override.showOnMobile;
    if (typeof override.name === "string") next.name = override.name;

    const notAllowed = normalizeNotAllowed(override.not_allowed || override.notAllowed);
    if (notAllowed) next.notAllowed = notAllowed;

    return next;
};

const isRouteExplicitlyEnabled = (override) => {
    return Boolean(override && typeof override === "object" && override.enabled === true);
};

/** Lowercase route override keys so "Pricing" matches route id "pricing". */
const normalizeRouteGroupOverrides = (groupOverrides) => {
    if (!groupOverrides || typeof groupOverrides !== "object") return {};
    const normalized = {};
    Object.entries(groupOverrides).forEach(([key, value]) => {
        if (key == null || key === "") return;
        normalized[String(key).toLowerCase()] = value;
    });
    return normalized;
};

const applyGroupOverrides = (groupRoutes, groupOverrides, enableAllRoute) => {
    const safeOverrides = normalizeRouteGroupOverrides(groupOverrides);
    return groupRoutes
        .map((route) => applyRouteOverride(route, safeOverrides[route.id]))
        .filter((route) => {
            if (route.enabled === false) {
                return false;
            }

            if (enableAllRoute) {
                return true;
            }

            return isRouteExplicitlyEnabled(safeOverrides[route.id]);
        });
};

const getSafeRouteConfig = (routeConfig) => {
    return routeConfig && typeof routeConfig === "object" ? routeConfig : {};
};

const ROUTE_CONFIG_GROUPS = ["private", "public", "shared", "client"];

/**
 * Partner (brand) as base; profile overrides win per route id when present.
 * Missing profile keys fall back to partner.
 */
export const mergeRouteConfigs = (brandRouteConfig, profileRouteConfig) => {
    const brand = getSafeRouteConfig(brandRouteConfig);
    const profile = getSafeRouteConfig(profileRouteConfig);

    const merged = {
        ...brand,
        ...profile,
        // Prefer profile top-level flags when set; otherwise keep brand
        base_home: profile.base_home ?? profile.baseHome ?? brand.base_home ?? brand.baseHome,
        enable_all_route:
            profile.enable_all_route ??
            profile.enableAllRoute ??
            brand.enable_all_route ??
            brand.enableAllRoute,
    };

    ROUTE_CONFIG_GROUPS.forEach((group) => {
        merged[group] = {
            ...normalizeRouteGroupOverrides(brand[group]),
            ...normalizeRouteGroupOverrides(profile[group]),
        };
    });

    return merged;
};

export const getConfiguredRoutes = (routeConfig = {}) => {
    const safeConfig = getSafeRouteConfig(routeConfig);
    // Accept both snake_case and camelCase from partner / profile APIs.
    const enableAllRoute = (safeConfig.enable_all_route ?? safeConfig.enableAllRoute) !== false;

    const configured = {
        private: applyGroupOverrides(baseRoutes.private, safeConfig.private, enableAllRoute),
        public: applyGroupOverrides(baseRoutes.public, safeConfig.public, enableAllRoute),
        shared: applyGroupOverrides(baseRoutes.shared, safeConfig.shared, enableAllRoute),
        client: applyGroupOverrides(baseRoutes.client, safeConfig.client, enableAllRoute),
    };

    // Sparse localhost / partner configs with enable_all_route:false and no
    // explicit enables would wipe every route — fall back to the full catalogue.
    const total =
        configured.private.length +
        configured.public.length +
        configured.shared.length +
        configured.client.length;

    if (total === 0 && enableAllRoute === false) {
        return getConfiguredRoutes({ ...safeConfig, enable_all_route: true, enableAllRoute: true });
    }

    return configured;
};

export const getActiveRouteConfig = ({ isAuthenticated, profileRouteConfig, brandRouteConfig }) => {
    if (isAuthenticated) {
        // Profile overrides partner per route; otherwise use partner defaults
        return mergeRouteConfigs(brandRouteConfig, profileRouteConfig);
    }

    return getSafeRouteConfig(brandRouteConfig);
};

export const resolveBaseHomePath = (routeConfig, configuredRoutes) => {
    // Never fall back to "/" — that path is only a redirect hub and loops on itself.
    const fallbackPath = configuredRoutes?.private.find((route) => route.path === URL_HOME)?.path
        || configuredRoutes?.private[0]?.path
        || configuredRoutes?.public.find((route) => route.path === URL_LOGIN)?.path
        || configuredRoutes?.public[0]?.path
        || configuredRoutes?.shared[0]?.path
        || configuredRoutes?.client[0]?.path
        || URL_HOME;
    const baseHomeStr = routeConfig?.base_home ?? routeConfig?.baseHome;

    if (typeof baseHomeStr !== "string" || baseHomeStr.trim().length === 0) {
        return fallbackPath;
    }

    const [group, id] = baseHomeStr.split(".");

    if (!group || !id || !configuredRoutes?.[group]) {
        return fallbackPath;
    }

    const targetRoute = configuredRoutes[group].find((route) => route.id === id);

    return targetRoute?.path || fallbackPath;
};
